import './button.scss';
import React, { useContext } from 'react';
import classnames from 'classnames';
import GlobalProps from '../globalProps';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/IconName';
import { Spinner } from '../Spinner/Spinner';
import { FormContext } from '../Form/Form';

export interface ButtonProps extends GlobalProps {
  title?: string;
  ariaLabel?: string;
  icon?: IconName;
  secondaryIcon?: IconName;
  elevated?: boolean;
  block?: boolean;
  type?:
    | 'primary'
    | 'secondary'
    | 'secondary-transparent'
    | 'secondary-dark'
    | 'dark'
    | 'link'
    | 'outline'
    | 'icon'
    | 'icon-simple'
    | 'icon-disabled'
    | 'automation-play'
    | 'automation-pause';
  condensed?: boolean;
  isSubmit?: boolean;
  disabled?: boolean;
  loading?: boolean;
  accessKey?: string;
  autoFocus?: boolean;
  tabIndex?: number;
  success?: boolean;
  onClick?: () => void;
  size?: 'small' | 'medium' | 'large' | 'overlay';
}

export function Button({
  title,
  ariaLabel,
  icon,
  secondaryIcon,
  block,
  elevated,
  type,
  isSubmit,
  disabled,
  loading,
  accessKey,
  autoFocus,
  tabIndex,
  onClick,
  className,
  style,
  success,
  condensed,
  size = 'medium',
}: ButtonProps): JSX.Element {
  const context = useContext(FormContext);
  function handleOnClick(event: React.MouseEvent): void {
    if (!disabled && !loading && (onClick || (isSubmit && context?.submit))) {
      event.preventDefault();
      event.stopPropagation();
      onClick?.();
      if (isSubmit) context?.submit?.();
    }
  }
  if (isSubmit && context) {
    disabled = disabled || context.submissionStatus === 'submitting';
    loading = loading || context.submissionStatus === 'submitting';
    success = success ?? context.submissionStatus === 'submitted';
  }

  return (
    <button
      className={classnames(
        'button',
        type ? `button--${type}` : '',
        `button--${size}`,
        {
          'button--icon': (icon || secondaryIcon) && !title,
          'button--elevated': elevated,
          'button--block': block,
          'button--disabled': disabled || loading,
          'button--loading': loading,
          'button--submitted': success,
          'button--condensed': condensed,
        },
        className
      )}
      style={style}
      onClick={handleOnClick}
      title={title}
      accessKey={accessKey}
      autoFocus={autoFocus}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      type={isSubmit ? 'submit' : 'button'}
    >
      {icon && <Icon name={icon} />}
      {title && <span className="button__text">{title}</span>}
      {secondaryIcon && <Icon name={secondaryIcon} />}
      {loading && (
        <span className="button__status">
          <Spinner scale="0.8" color="white" />
        </span>
      )}
      {success && (
        <span className="button__status">
          <Icon name="check" />
        </span>
      )}
    </button>
  );
}
